'use client';

import { ColumnDef, Row } from '@tanstack/react-table';

import { useCustomersTableCtx } from '@/app/(app)/customers/table/provider';
import { PhoneNumber } from '@/components/atoms/phonenumber';
import { Link } from '@/components/link';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatAddress } from '@/lib/format';
import { cn } from '@/lib/utils';

type Customer = ApiSchemas['CustomerRead'];

export const CUSTOMER_COLUMNS: ColumnDef<Customer>[] = [
  // {
  //   id: 'select',
  //   header: ({ table }) => (
  //     <Checkbox
  //       checked={table.getIsAllRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
  //       onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
  //       aria-label='Select all'
  //     />
  //   ),
  //   cell: ({ row }) => (
  //     <Checkbox
  //       checked={row.getIsSelected()}
  //       onCheckedChange={row.getToggleSelectedHandler()}
  //       onClick={(e) => e.stopPropagation()}
  //       aria-label='Select row'
  //     />
  //   ),
  //   enableSorting: false,
  //   enableHiding: false,
  // },
  {
    header: () => <div className='pl-2'>Name</div>,
    accessorKey: 'name',
    cell: ({ row }) => <CustomerNameCell row={row} />,
    enableHiding: false,
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      const customer = row.original;
      return (
        <Badge
          variant='outline'
          className={cn(
            'border-none',
            customer.status === 'ACTIVE' && 'bg-green-500/10 text-green-500',
            customer.status === 'INACTIVE' && 'bg-red-500/10 text-red-500'
          )}
        >
          {customer.status}
        </Badge>
      );
    },
  },
  {
    header: 'Email',
    accessorKey: 'email',
    cell: ({ row }) => {
      const primary = row.original.contacts.find((c) => c.primary && c.type === 'EMAIL');
      if (primary) {
        return <>{primary.value}</>;
      }
      const email = row.original.contacts.find((c) => c.type === 'EMAIL');
      if (email) {
        return <>{email.value}</>;
      }
      return null;
    },
  },
  {
    header: 'Phone',
    accessorKey: 'phone',
    cell: ({ row }) => {
      const primary = row.original.contacts.find((c) => c.primary && c.type === 'PHONE');
      if (primary) {
        return <PhoneNumber value={primary.value} />;
      }
      const phone = row.original.contacts.find((c) => c.type === 'PHONE');
      if (phone) {
        return <PhoneNumber value={phone.value} />;
      }
      return null;
    },
  },
  {
    header: 'Address',
    accessorKey: 'address',
    cell: ({ row }) => {
      const primary_location = row.original.locations.find((location) => location.primary) ?? row.original.locations[0];
      if (primary_location) {
        return <>{formatAddress(primary_location)}</>;
      }
      return null;
    },
  },
];

function CustomerNameCell({ row }: { row: Row<Customer> }) {
  const { setActiveId } = useCustomersTableCtx();
  const companyName = row.original.company ?? '';
  const fullName = `${row.original.first_name} ${row.original.last_name}`.trim();
  const noName = !companyName && !fullName;

  return (
    <div className='flex items-center gap-2 pl-2'>
      <Link
        href={`/customers/${row.original.id}`}
        onClick={() => setActiveId(row.original.id)}
        className='space-y-1 font-medium hover:cursor-pointer hover:underline'
      >
        {companyName && <div className='text-sm leading-none'>{companyName}</div>}
        {fullName && (
          <div className={cn('text-sm leading-none', companyName && 'text-xs text-muted-foreground')}>{fullName}</div>
        )}
        {noName && <div className='text-sm text-muted-foreground'>No name</div>}
      </Link>
      {row.original.status === 'INACTIVE' && (
        <Tooltip>
          <TooltipTrigger>
            <div className='size-2 rounded-full bg-destructive' />
          </TooltipTrigger>
          <TooltipContent>
            <p>This customer is inactive</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
